import ApiClient from './ApiClient.js';
import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';

/**
 * Account service. Every visitor silently becomes a guest account, restored
 * on each visit via a persistent deviceId — until they validate the account
 * (username/email/password) or log into an existing one.
 * Fully non-blocking: offline, the game keeps working on localStorage and
 * `this.user` stays null.
 */
export default class AuthService {
  constructor() {
    this.api = new ApiClient();
    this.user = null;
    this.onChange = null; // (user|null) => void, wired by the UI
  }

  get isOnline() {
    return this.user !== null;
  }

  _deviceId() {
    let id = storageGet(STORAGE.DEVICE_ID);
    if (!id) {
      id = crypto.randomUUID();
      storageSet(STORAGE.DEVICE_ID, id);
    }
    return id;
  }

  _setUser(user) {
    this.user = user;
    this.onChange?.(user);
  }

  /** Silent login: existing token first, otherwise guest by deviceId. */
  async init() {
    if (this.api.token) {
      const me = await this.api.get('/auth/me');
      if (me.ok && me.data?.user) {
        this._setUser(me.data.user);
        return this.user;
      }
      if (me.status === 401 || me.status === 403 || me.status === 404) {
        this.api.setToken(null); // stale token: fall through to guest
      } else if (me.status === 0) {
        return null; // offline: keep the token for next time
      }
    }
    return this._guest();
  }

  async _guest() {
    const res = await this.api.post('/auth/guest', { deviceId: this._deviceId() });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return this.user;
    }
    return null;
  }

  /** Start an anti-cheat play session before a song begins. */
  async beginSession(songId, difficulty) {
    if (!this.isOnline) return null;
    const res = await this.api.post('/game/session/begin', { songId, difficulty });
    return res.ok ? res.data : null;
  }

  /** Guest -> named account. Returns { ok, message }. */
  async convert({ username, email, password }) {
    const res = await this.api.post('/auth/convert', { username, email, password });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Serveur injoignable — réessaie plus tard.' };
  }

  /** Log into an existing account (scores follow the account). */
  async login({ username, password }) {
    const res = await this.api.post('/auth/login', { username, password });
    if (res.ok && res.data?.token) {
      this.api.setToken(res.data.token);
      this._setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.data?.message || 'Serveur injoignable — réessaie plus tard.' };
  }

  /** Logout: drops the token and falls back to a (new or restored) guest. */
  async logout() {
    this.api.setToken(null);
    this._setUser(null);
    return this._guest();
  }

  async resendVerification() {
    const res = await this.api.post('/auth/resend-verification');
    return { ok: res.ok, message: res.data?.message };
  }

  /** Submit a completed run — requires the sessionToken from beginSession. */
  async submitScore({ songId, difficulty, score, maxCombo, grade, sessionToken }) {
    if (!this.isOnline || !sessionToken) return { ok: false };
    const res = await this.api.post('/scores', {
      songId,
      difficulty,
      score,
      maxCombo,
      grade,
      sessionToken
    });
    return { ok: res.ok, improved: res.data?.improved ?? false, rank: res.data?.rank ?? null };
  }

  /** All my best scores (one per song/difficulty), for the catalog badges. */
  async getMyScores() {
    if (!this.isOnline) return null;
    const res = await this.api.get('/scores/me');
    return res.ok ? (res.data?.scores ?? []) : null;
  }

  async getLeaderboard(songId, difficulty) {
    const res = await this.api.get(`/scores/leaderboard/${encodeURIComponent(songId)}?difficulty=${encodeURIComponent(difficulty)}`);
    return res.ok ? res.data : null;
  }

  /** Publish a finished run's universe to the shared gallery (guests too). */
  async submitUniverse({ universe, songId, score }) {
    if (!this.isOnline || !universe) return { ok: false };
    const res = await this.api.post('/universes', { universe, songId, score });
    return { ok: res.ok, id: res.data?.id ?? null };
  }

  /** Shared gallery: sort = 'top' (like ranking) | 'recent' | 'mine'. */
  async getUniverses(sort = 'top') {
    const res = await this.api.get(`/universes?sort=${encodeURIComponent(sort)}`);
    return res.ok ? (res.data?.universes ?? []) : null;
  }

  /** Toggle my like on a universe. Returns { liked, likes } or null offline. */
  async toggleUniverseLike(id) {
    if (!this.isOnline) return null;
    const res = await this.api.post(`/universes/${encodeURIComponent(id)}/like`);
    return res.ok ? res.data : null;
  }
}
